import { useCallback } from 'react';

import {
  LeadSavedViewsQuery,
  useDeleteLeadSavedViewMutation,
  useLeadSavedViewsQuery,
  useSaveLeadSavedViewMutation,
} from 'core/graphql';

import { ContractsView } from './contractsBoardModel';
import {
  contractsFiltersToView,
  IContractsFilters,
} from './contractsViewsModel';
import { useEsignPermissions } from './useEsignPermissions';

/**
 * ТЗ 0120, этап 2 — сохранённые виды «Договоров»: та же модель, что у
 * лидов (`LeadSavedView`), со `scope: contracts`. Роли — экрана «Договоры»;
 * общий вид заводит и удаляет только Admin (сервер проверяет то же).
 */

export type IContractSavedView = LeadSavedViewsQuery['leadSavedViews'][number];

const NO_VIEWS: IContractSavedView[] = [];

export interface IContractViewWriteResult {
  ok: boolean;
  /** `customErrors.code` или `NETWORK`. */
  code: string | null;
  message: string | null;
}

const REFETCH = ['LeadSavedViews'];

const VIEWS_SCOPE = { scope: 'contracts' };

export const useContractSavedViews = (enabled = true) => {
  const { canOpenPage, isAdmin } = useEsignPermissions();
  const { data, loading, error: loadError, refetch } = useLeadSavedViewsQuery({
    variables: VIEWS_SCOPE,
    skip: !canOpenPage || !enabled,
    fetchPolicy: 'cache-and-network',
  });
  const [saveMut, { loading: saving }] = useSaveLeadSavedViewMutation();
  const [deleteMut, { loading: deleting }] = useDeleteLeadSavedViewMutation();

  const save = useCallback(
    async (input: {
      name: string;
      filters: IContractsFilters;
      view: ContractsView;
      shared: boolean;
    }): Promise<IContractViewWriteResult> => {
      try {
        const { data: res } = await saveMut({
          variables: {
            ...VIEWS_SCOPE,
            name: input.name.trim(),
            filters: contractsFiltersToView(input.filters),
            view: input.view,
            shared: isAdmin && input.shared,
          },
          refetchQueries: REFETCH,
          awaitRefetchQueries: true,
        });
        const payload = res?.saveLeadSavedView;
        const error = payload?.customErrors?.find(Boolean);
        if (payload?.success && !error) {
          return { ok: true, code: null, message: null };
        }
        return {
          ok: false,
          code: error?.code || 'ERROR',
          message: error?.message || null,
        };
      } catch (caught) {
        return { ok: false, code: 'NETWORK', message: null };
      }
    },
    [isAdmin, saveMut],
  );

  const remove = useCallback(
    async (id: string): Promise<IContractViewWriteResult> => {
      try {
        const { data: res } = await deleteMut({
          variables: { ...VIEWS_SCOPE, id },
          refetchQueries: REFETCH,
          awaitRefetchQueries: true,
        });
        const payload = res?.deleteLeadSavedView;
        const error = payload?.customErrors?.find(Boolean);
        if (payload?.success && !error) {
          return { ok: true, code: null, message: null };
        }
        return {
          ok: false,
          code: error?.code || 'ERROR',
          message: error?.message || null,
        };
      } catch (caught) {
        return { ok: false, code: 'NETWORK', message: null };
      }
    },
    [deleteMut],
  );

  const views = data?.leadSavedViews ?? NO_VIEWS;
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  return {
    views,
    loading: loading && !data,
    failed: Boolean(loadError) && !data,
    reload,
    canShare: isAdmin,
    save,
    remove,
    busy: saving || deleting,
  };
};
